import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { InputActionMeta } from "react-select";

import { useGetCompanyContactBook } from "api/queries/company/useGetCompanyContactBook";
import useDebounce from "hooks/useDebounce";
import { usePersistScheduleDraft } from "@RequestFormV2/hooks/usePersistScheduleDraft";
import { useRequestFormSaveOrNull } from "@RequestFormV2/context/save";
import { useSchedule } from "@RequestFormV2/sections/Schedule/context";
import { buildContactSelectOptions, ContactBookOption } from "@RequestFormV2/sections/Schedule/utils/contactBook";
import type { SharedContactFieldPaths } from "@RequestFormV2/sections/Schedule/utils/sharedAddressFieldPaths";

const CONTACT_SEARCH_DEBOUNCE_MS = 400;

export function useSharedContact(fieldPaths: SharedContactFieldPaths, fieldsDisabled: boolean) {
  const { setValue } = useFormContext();
  const saveContext = useRequestFormSaveOrNull();
  const { location } = useSchedule();
  const persistScheduleDraft = usePersistScheduleDraft();
  const [contactSearchQuery, setContactSearchQuery] = useState("");

  const debouncedContactQuery = useDebounce(
    contactSearchQuery.length >= 3 ? contactSearchQuery : "",
    CONTACT_SEARCH_DEBOUNCE_MS,
  );

  const isSearching = contactSearchQuery.length >= 3;
  const searchQuery = isSearching ? debouncedContactQuery : undefined;

  const { data: contactSearchResults = [], isFetching: isLoadingContacts } = useGetCompanyContactBook(
    {
      location: location ?? "",
      ...(searchQuery && { query: searchQuery }),
      stopIndex: -1,
    },
    { enabled: !!location && !fieldsDisabled, keepPreviousData: isSearching },
  );

  useEffect(() => {
    setContactSearchQuery("");
  }, [location]);

  const contactName = useWatch({ name: fieldPaths.contact_name });

  const contactSelectOptions = useMemo(
    () => buildContactSelectOptions({ mapped: contactSearchResults, contactName }),
    [contactSearchResults, contactName],
  );

  const handleSelectContact = useCallback(
    (contact: ContactBookOption) => {
      if (!contact || fieldsDisabled) return;
      setValue(fieldPaths.contact_name, contact.label);
      // Free-typed synthetic options only have label/value — do not wipe phone/email.
      if (!("phone_number" in contact) && !("email" in contact)) return;
      setValue(fieldPaths.contact_phone, contact.phone_number ?? "");
      setValue(fieldPaths.contact_email, contact.email ?? "");
    },
    [setValue, fieldsDisabled, fieldPaths],
  );

  const handleContactSelect = useCallback(
    (val: string | null) => {
      if (fieldsDisabled) return;
      if (!val) {
        setContactSearchQuery("");
        setValue(fieldPaths.contact_name, "");
        setValue(fieldPaths.contact_phone, "");
        setValue(fieldPaths.contact_email, "");
        persistScheduleDraft();
        return;
      }
      const contact = contactSelectOptions.find((c) => c.value === val);
      if (contact) {
        handleSelectContact(contact);
        persistScheduleDraft();
      }
    },
    [contactSearchResults, handleSelectContact, fieldsDisabled, fieldPaths, setValue, persistScheduleDraft],
  );

  const handleContactNameInputChange = useCallback(
    (val: string, meta: InputActionMeta) => {
      if (fieldsDisabled) return;
      if (meta.action === "clear" || (meta.action === "set-value" && !val)) {
        setContactSearchQuery("");
        return;
      }
      if (meta.action === "input-blur") {
        persistScheduleDraft();
        return;
      }
      if (meta.action !== "input-change") return;
      setValue(fieldPaths.contact_name, val);
      setContactSearchQuery(val);
      saveContext?.saveField(fieldPaths.contact_name, val).catch(() => undefined);
    },
    [fieldsDisabled, fieldPaths.contact_name, setValue, saveContext, persistScheduleDraft],
  );

  return {
    fieldPaths,
    contactSearchResults: contactSelectOptions,
    isLoadingContacts,
    handleContactSelect,
    handleContactNameInputChange,
    fieldsDisabled,
  };
}

export type SharedContactSlice = ReturnType<typeof useSharedContact>;
